// src/com/user_info/UserInfoInputMine.js
import React, { useEffect, useRef, useState } from 'react';
import {
    Button, DialogContent, DialogActions, CircularProgress, Grid, Popover
} from '@mui/material';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import { AppWord, AppObject, AppStyle, InputType } from '../../value/AppConst';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import DraggableEditor from '../../common/DraggableEditor';
import InputUtil from '../../value/InputUtil';
import VzmTextField from '../../common/VzmTextField';
import { ErrorHintBox, VzmPrint } from '../../common/VzmStyled';
import DaumAddressSearch from '../../common/DaumAddressSearch';
import PhoneField from '../../common/PhoneField';
import { UserInfoDictionary } from './UserInfoDict';
import { useAlertSetter } from '../../api/app-hook';
import { userInfoRepo } from '../../value/AppModel';
import { EDIT_MY_USER_INFO, GET_USER_INFO } from './UserInfoGql';
import IsValid from '../../value/IsValid';

const InputMap = {
    // toAdd, toEdit : mark if need to submit for mutation
    userName : {
        id: 'userName', label: '이름',
        type: InputType.text, required: true, toAdd: true, toEdit: true,
        help: '12자 이내로 입력하세요.',
        check: (value) => {return IsValid.string(value, 0, 36)},
    },
    password : {
        id: 'password', label: '암호',
        type: InputType.password, required: true, toAdd: true, toEdit: true,
        help: '본인 확인을 위한 암호를 입력하세요.',
        check: (value) => {return IsValid.string(value, 1, 50)},
    },
    zipCd : {
        id: 'zipCd', label: '우편번호',
        type: InputType.text, required: false, toAdd: true, toEdit: true,
        check: (value) => {return IsValid.string(value, 0, 7)},
    },
    addr : {
        id: 'addr', label: '주소',
        type: InputType.text, required: false, toAdd: true, toEdit: true,
        check: (value) => {return IsValid.string(value, 0, 120)},
    },
    telno : {
        id: 'telno', label: '연락처',
        type: InputType.text, required: false, toAdd: true, toEdit: true,
        check: (value) => {return IsValid.string(value, 0, 15)},
    },
    /* mobile : {
        id: 'mobile', label: 'mobile',
        type: InputType.text, required: false, toAdd: true, toEdit: true,
        help: '64자 이내로 입력하세요.',
        check: (value) => {return IsValid.string(value, 0, 64)},
    },*/
    email : {
        id: 'email', label: '메일주소',
        type: InputType.email, required: false, toAdd: true, toEdit: true,
        help: '형식에 맞도록 메일주소를 입력하세요.',
        check: (value) => {return IsValid.nullString(value) || IsValid.email(value)},
    },
};

/**
 * (USER_INFO) 내 정보 수정
 * Project: NewTOP for cloud
 */
export default function UserInfoInputMine({
    open,
    onClose,
}) {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const [item, setItem] = useState({});
    const [input, setInput] = useState({...AppObject.emptyInputData});
    const [anchorAddr, setAnchorAddr] = useState(null);
    const openConfirmDialog = useConfirmDialog();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const addAlert = useAlertSetter();
    const userInfoReadRef = useRef(false);
    const [getUserInfoItem, responseItem] = useLazyQuery(GET_USER_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetItem(data, option)},
        onError: (error) => addAlert({title: "내 정보 조회 오류", message: [error.message]})
    });
    const [editMyUserInfo, responseEdit] = useMutation( EDIT_MY_USER_INFO, {
        onCompleted: (data, option) => onCompleteEdit(data, option),
        onError: (error) => addAlert({title: "내 정보 수정 오류", message: [error.message]})
    } );

    // ##### Call GraphQL to get List #####
    const isEdit = true; // always edit mode

    useEffect(()=>{
        if(open) {
            if(!userInfoReadRef.current) {
                getUserInfoItem({variables: {userNid: sessionInfo.user.userNid}});
                userInfoReadRef.current = true;
            }
        }
        else userInfoReadRef.current = false;
    }, [open, sessionInfo, getUserInfoItem]);

    useEffect(()=>{
        if(open) {
            const inputSet = {...AppObject.emptyInputData};
            if(item.userId) {
                inputSet.data = {...item};
            }
            setInput(inputSet);
        }
    }, [open, item]);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    
    const onCompleteGetItem = (data, option) => {
        if(data.userInfoItem) {
            setItem(data.userInfoItem);
        }
        else addAlert({title:UserInfoDictionary.errorTitle.Get, message:['사용자 정보를 찾을 수 없습니다!!']});
    };
    
    const onCompleteEdit = (data, clientOption) => {
        if(data.userInfoEditMine.ok) {
            enqueueSnackbar('사용자 정보가 수정되었습니다.', {variant: 'success', autoHideDuration: 2000});
            onClose();
        }
        else addAlert({title:"내 정보 수정 오류", message:[data.userInfoEditMine.message]});
    };

    const onChangeValue = (id, value, error) => {
        const newInput = InputUtil.reflectData(input, id, value, error, InputMap, isEdit);

        setInput(newInput);
    };

    const onSelectAddress = (addr) => {
        if(addr) {
            const newInputOne = InputUtil.reflectData(input, InputMap.zipCd.id, addr.zipCd, !InputMap.zipCd.check(addr.zipCd), InputMap, isEdit);
            const newInput = InputUtil.reflectData(newInputOne, InputMap.addr.id, addr.addr, !InputMap.addr.check(addr.addr), InputMap, isEdit);
            setInput(newInput);
        }
        setAnchorAddr(null);
    };

    const onClickSubmit = () => {
        const param = InputUtil.getDataToSubmit(input.data, InputMap, isEdit);

        editMyUserInfo({variables: {userInfo: param}});
    };

    const askCancel = async () => {
        const confirm = await openConfirmDialog({
            ...AppObject.messageCancelEdit,
            option: {yesLabel: AppWord.IgnoreAndClose}
        });
        if(confirm) onClose();
    };

    const onClickCancel = () => {
        if(input.changed) askCancel();
        else onClose();
    };

    const loadingAny = responseEdit.loading || responseItem.loading;

    return(
        <DraggableEditor open={open} maxWidth='sm'
            startIcon={<AccountBoxIcon fontSize={AppWord.LARGE} style={{color:'white', marginRight:15}}/>}
            title="내 정보 수정"
        >
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <VzmPrint label="사용자 ID"
                            value={item.userId}
                        />
                    </Grid>
                    <Grid item xs={6}>
                            <VzmTextField
                                dict={InputMap.password}
                                value={input.data.password}
                                onChange={onChangeValue}
                                fullWidth={true}
                            />
                    </Grid>
                    <Grid item xs={6}>
                        <span style={{
                                fontSize: '0.8em', color: '#f88',
                                marginTop: 10, display: 'inline-block'
                            }}
                        >
                            본인 확인을 위해 암호를 입력하세요.
                        </span>
                    </Grid>
                    <Grid item xs={12}>
                        <VzmTextField
                            dict={InputMap.userName}
                            value={input.data.userName}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>



                    <Grid item xs={6}>
                        <VzmTextField
                            dict={InputMap.zipCd}
                            value={input.data.zipCd}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Button
                            style={{marginLeft:8}} size={AppWord.LARGE}
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.primary}
                            onClick={(e)=>{setAnchorAddr(e.currentTarget);}}
                        >
                            주소찾기
                        </Button>
                        <Popover
                            anchorEl={anchorAddr}
                            open={Boolean(anchorAddr)}
                            onClose={()=>{setAnchorAddr(null);}}
                            anchorOrigin={{ vertical: 'top', horizontal: 'right', }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right', }}
                        >
                            <DaumAddressSearch open={Boolean(anchorAddr)} onSelect={onSelectAddress}/>
                        </Popover>
                    </Grid>

                    <Grid item xs={12}>
                        <VzmTextField
                            dict={InputMap.addr}
                            value={input.data.addr}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>


                    <Grid item xs={6}>
                        <PhoneField
                            dict={InputMap.telno}
                            value={input.data.telno}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={6}></Grid>
                    <Grid item xs={12}>
                        <VzmTextField
                            dict={InputMap.email}
                            value={input.data.email}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                
                </Grid>
            </DialogContent>
            <DialogActions style={{display:'flex'}}>
                {
                    input.errorLabels && input.errorLabels.length > 0
                    ?
                    <ErrorHintBox>
                        확인: {input.errorLabels.join(', ')}
                    </ErrorHintBox>
                    : null
                }
                {
                    responseEdit.loading
                    ?
                    <span>
                        <CircularProgress />
                        &nbsp;데이터 저장 중...
                    </span>
                    :
                    <>
                        <Button
                            onClick={onClickSubmit}
                            disabled={input.hasError || !input.changed || loadingAny}
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.primary}
                        >저장</Button>
                        <Button onClick={onClickCancel}
                            color={AppStyle.mui.warning}
                            variant={AppStyle.mui.contained}
                        >취소</Button>
                    </>
                }
            </DialogActions>
        </DraggableEditor>
    );
}
